import { Component, inject, output } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { PasswordValidators } from 'ngx-validators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PasswordResetService } from '../../service/password-reset.service';
import { hasErrorAndTouched } from '@shared/utils';
import { SharedModule } from '@shared/shared.module';
import { passwordMatchValidator } from '@core/validators';
import { ValidationMessageService } from '@shared/validators';

@UntilDestroy()
@Component({
  selector: 'app-new-password-form',
  templateUrl: './new-password-form.component.html',
  styleUrls: ['./new-password-form.component.scss'],
  imports: [SharedModule],
})
export class NewPasswordFormComponent {
  readonly submitSuccessEvent = output<boolean>();

  readonly hasErrorAndTouched = hasErrorAndTouched;
  protected readonly validationMessageService = inject(ValidationMessageService);

  isSubmitting = false;
  passwordResetForm = this.fb.group({
    password: [
      '',
      [
        Validators.required,
        Validators.minLength(8),
        PasswordValidators.alphabeticalCharacterRule(1),
        PasswordValidators.digitCharacterRule(1),
        PasswordValidators.lowercaseCharacterRule(1),
        PasswordValidators.uppercaseCharacterRule(1),
        PasswordValidators.specialCharacterRule(1),
      ],
    ],
    confirmedPassword: new FormControl('', {
      validators: [Validators.required, passwordMatchValidator()],
    }),
  });

  constructor(
    private readonly fb: FormBuilder,
    private readonly passwordResetService: PasswordResetService,
  ) {}

  /**
   * Check form validity to submit.
   */
  get disableSubmitForm(): boolean {
    return this.passwordResetForm.invalid || this.isSubmitting;
  }

  /**
   * Check password value validity except required.
   */
  get hasOtherPasswordErrors(): boolean {
    const validators = [
      'minLength',
      'alphabeticalCharacterRule',
      'digitCharacterRule',
      'lowercaseCharacterRule',
      'uppercaseCharacterRule',
      'specialCharacterRule',
    ];
    return validators.some((validator) => this.hasErrorAndTouched(this.passwordResetForm.get('password'), validator));
  }

  /**
   * Submit new password form.
   */
  onSubmit() {
    this.isSubmitting = true;
    const { password } = this.passwordResetForm.value;

    if (!password) return;

    this.passwordResetService
      .changePassword(password)
      .pipe(untilDestroyed(this))
      .subscribe((res) => {
        this.passwordResetForm.reset();
        this.isSubmitting = false;
        this.submitSuccessEvent.emit(res.success);
      });
  }
}
